import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, RefreshCw, CheckCircle2, AlertTriangle, Download, Truck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/csv";

function Stat({ label, value, accent, testId }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</p>
      <p className={`font-serif text-3xl mt-1 ${accent ? "text-[#7A2021]" : "text-[#1A1714]"}`} data-testid={testId}>
        {(value ?? 0).toLocaleString()}
      </p>
    </div>
  );
}

export default function BulkTrackingDialog({ open, onOpenChange, onUpdated }) {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setFile(null);
    setJob(null);
    setStarting(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => { reset(); onOpenChange(false); };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPoll = (job_id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/orders/bulk-tracking/${job_id}`);
        setJob(data);
        if (data.done) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (data.status === "done") {
            toast.success(`Updated ${data.updated} · sent ${data.emails_sent} emails · skipped ${data.skipped}`);
            onUpdated?.();
          } else if (data.status === "error") {
            toast.error(data.error || "Bulk tracking failed");
          }
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.error("Lost connection to the job.");
      }
    }, 500);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/orders/bulk-tracking", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const initial = {
        job_id: data.job_id,
        total: data.total,
        processed: 0, updated: 0, skipped: 0, not_found: 0,
        emails_sent: 0, email_failed: 0,
        progress: 0, status: "queued", done: false,
      };
      setJob(initial);
      startPoll(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start bulk-tracking job");
      reset();
    } finally {
      setStarting(false);
    }
  };

  const progressPct = job ? Math.min(100, Math.max(0, Math.round(job.progress || 0))) : 0;
  const done = job?.done;
  const okDone = job?.status === "done";

  const downloadReport = async () => {
    if (!job?.job_id) return;
    try {
      const resp = await api.get(`/admin/orders/bulk-tracking/${job.job_id}/report`, {
        responseType: "blob",
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(resp.data, `bulk-tracking-report-${stamp}.csv`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download report");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="bulk-tracking-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl text-[#1A1714] flex items-center gap-2">
            <Truck size={22} /> Bulk tracking update (CSV or Excel)
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Accepts .csv or .xlsx. Required columns: order_id, tracking_number. Optional: carrier (ups / usps / fedex / dhl / other).
            Orders already carrying the same tracking number are skipped. Every updated order is emailed a shipped notification.
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <div className="p-10 flex-1 flex flex-col items-center justify-center">
            <label
              htmlFor="bulk-tracking-input"
              className="cursor-pointer border-2 border-dashed border-[#E5E1D8] hover:border-[#1A1714] w-full max-w-xl p-16 text-center transition-colors"
              data-testid="bulk-tracking-drop"
            >
              <Upload className="mx-auto text-[#1A1714]/40" size={40} />
              <p className="mt-4 font-serif text-2xl text-[#1A1714]">Drop a CSV or Excel file, or click to choose</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
                Required columns: order_id · tracking_number · Optional: carrier
              </p>
              <input
                id="bulk-tracking-input"
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onFile}
                className="hidden"
                data-testid="bulk-tracking-file-input"
              />
            </label>
            {starting && <p className="mt-6 font-serif italic text-lg text-[#1A1714]/60">Queueing job…</p>}
          </div>
        )}

        {job && (
          <div className="p-6 flex-1 flex flex-col gap-6" data-testid="bulk-tracking-status">
            <div>
              <div className="flex justify-between items-baseline">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  {done ? (okDone ? "Complete" : "Failed") : "Processing"} · {file?.name}
                </p>
                <p className="font-mono text-xs text-[#1A1714]" data-testid="bulk-tracking-progress-label">
                  {job.processed?.toLocaleString?.() ?? job.processed} / {job.total?.toLocaleString?.() ?? job.total}
                </p>
              </div>
              <Progress
                value={progressPct}
                className="mt-3 h-3 bg-[#E5E1D8] rounded-none [&>div]:bg-[#7A2021] [&>div]:rounded-none"
                data-testid="bulk-tracking-progress"
              />
              <div className="mt-2 flex justify-between items-baseline font-mono text-[10px] uppercase tracking-editorial">
                <span className="text-[#1A1714]/50">Processing one row at a time (sends email per update)</span>
                <span className="text-[#7A2021]" data-testid="bulk-tracking-progress-pct">{progressPct}%</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 border-y border-[#E5E1D8] py-6">
              <Stat label="Rows" value={job.total} testId="bulk-tracking-stat-total" />
              <Stat label="Updated" value={job.updated} accent testId="bulk-tracking-stat-updated" />
              <Stat label="Skipped" value={job.skipped} testId="bulk-tracking-stat-skipped" />
              <Stat label="Not found" value={job.not_found} testId="bulk-tracking-stat-notfound" />
              <Stat label="Emails sent" value={job.emails_sent} testId="bulk-tracking-stat-emails" />
            </div>

            <div className="flex-1" />

            <div className="flex justify-between items-center gap-3 border-t border-[#E5E1D8] pt-5">
              {!done ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 flex items-center gap-2">
                    <RefreshCw size={12} className="animate-spin" /> Running — safe to leave this open
                  </p>
                  <Button
                    disabled
                    className="rounded-none bg-[#1A1714] text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial opacity-60"
                  >
                    Processing…
                  </Button>
                </>
              ) : (
                <>
                  <p className={`font-serif text-lg flex items-center gap-2 ${okDone ? "text-[#1A1714]" : "text-[#7A2021]"}`}>
                    {okDone ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                    {okDone
                      ? <>Done. <em className="text-[#7A2021]">{job.updated}</em> updated · <em>{job.emails_sent}</em> emailed · <em>{job.skipped}</em> skipped · <em>{job.not_found}</em> not found</>
                      : <>Job failed: {job.error}</>}
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    {okDone && (
                      <Button
                        onClick={downloadReport}
                        variant="outline"
                        className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                        data-testid="bulk-tracking-download-report"
                      >
                        <Download size={14} className="mr-2" /> Download report
                      </Button>
                    )}
                    <Button
                      onClick={reset}
                      variant="outline"
                      className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                      data-testid="bulk-tracking-again"
                    >
                      Upload another
                    </Button>
                    <Button
                      onClick={close}
                      className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
                      data-testid="bulk-tracking-close"
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
